import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Paper,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Chip
} from '@mui/material';
import swal from 'sweetalert';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip as RechartsTooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useNavigate } from 'react-router-dom';

import { useEntity } from '../../context/EntityContext';
import useAuth from '../../hooks/useAuth';
import { getPublicationsDashboard } from '../../services/dashboardService';
import { DashboardPublicationsResponse } from '../../types/dashboard';
import AssignmentIcon from '@mui/icons-material/Assignment';
import AddCircleIcon from '@mui/icons-material/AddCircle';

/**
 * Formata a data/hora em português (por extenso), sem inserir "às" manualmente.
 */
function formatFullDateTime(dateString: string | null): string {
  if (!dateString) return '—';
  try {
    const date = new Date(dateString);
    // Exemplo: "3 de janeiro de 2025 13:40" (pt-BR).
    return new Intl.DateTimeFormat('pt-BR', {
      dateStyle: 'long',
      timeStyle: 'short',
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    }).format(date);
  } catch {
    return dateString;
  }
}

const Dashboard: React.FC = () => {
  const [data, setData] = useState<DashboardPublicationsResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const { selectedEntity } = useEntity();
  const { user } = useAuth();
  const navigate = useNavigate();

  async function fetchDashboard() {
    setLoading(true);
    try {
      if (!selectedEntity) return;
      const response = await getPublicationsDashboard(selectedEntity);
      setData(response);
    } catch (error) {
      console.error('Erro ao buscar dados da dashboard:', error);
      swal('Erro', 'Não foi possível carregar os dados da dashboard.', 'error');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (selectedEntity) {
      fetchDashboard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEntity]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!selectedEntity) {
    return (
      <Typography variant="body1" sx={{ mt: 4 }}>
        Selecione uma entidade para visualizar a dashboard de publicações.
      </Typography>
    );
  }

  if (!data) {
    return (
      <Typography variant="body1" sx={{ mt: 4 }}>
        Nenhum dado disponível ou houve um erro ao carregar.
      </Typography>
    );
  }

  // Preparar dados p/ gráficos
  const categoryData = data.categoryRepresentation.map((cat) => ({
    name: cat.category_name,
    quantidade: cat.count,
    percentage: cat.percentage
  }));

  // Mudança: agora é user_name, então não podemos mais usar user_id
  const publicationsByUserData = data.publicationsByUser.map((pu) => {
    let userName = pu.user_name || 'Usuário (desconhecido)';
    // Se quiser, pode comparar com user?.full_name
    // Mas depende do que for desejado no front.
    return {
      name: userName,
      quantidade: pu.count,
      percentage: pu.percentage
    };
  });

  const pieColors = ['#6A1B9A', '#FF914D', '#4caf50', '#f44336', '#ffeb3b', '#2196f3'];

  // Clique na publicação -> vai para /publicacoes/:publicationId
  const handleRowClick = (pub: any) => {
    navigate(`/publicacoes/${pub.id}`);
  };

  // Cores para as categorias nos chips
  const colorArray = ['primary','secondary','success','error','warning','info'] as const;

  return (
    <Box sx={{ mt: 3 }}>
      <Box sx={{ display: 'flex', gap: 2, my: 2 }}>
        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          onClick={() => navigate('/publicacoes/nova')}
          sx={{ minWidth: 180 }}
        >
          Nova Publicação
        </Button>
        <Button
          variant="contained"
          startIcon={<AssignmentIcon />}
          onClick={() => navigate('/publicacoes')}
          sx={{ minWidth: 180 }}
        >
          Ver todas
        </Button>
      </Box>

      {/* Cards com informações */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 3,
          mb: 3
        }}
      >
        {/* (A) Card: Total de Publicações */}
        <Paper
          sx={{
            p: 2,
            flex: '1 1 250px',
            minWidth: 250,
            boxShadow: 4,
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: 8,
            },
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Total de Publicações
          </Typography>
          <Box
            sx={{
              flexGrow: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="h3" color="primary">
              {data.totalPublications}
            </Typography>
          </Box>
        </Paper>

        {/* (B) Categorias (Pizza) */}
        <Paper
          sx={{
            p: 2,
            flex: '1 1 300px',
            minWidth: 300,
            boxShadow: 4,
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: 8,
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Categorias
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <PieChart>
              <Pie
                data={categoryData}
                dataKey="quantidade"
                nameKey="name"
                outerRadius={80}
                label
              >
                {categoryData.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={pieColors[index % pieColors.length]}
                  />
                ))}
              </Pie>
              <RechartsTooltip />
            </PieChart>
          </ResponsiveContainer>
        </Paper>

        {/* (C) Publicações por Usuário (Barras) */}
        <Paper
          sx={{
            p: 2,
            flex: '1 1 300px',
            minWidth: 300,
            boxShadow: 4,
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
              transform: 'scale(1.03)',
              boxShadow: 8,
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            Publicações por Usuário
          </Typography>
          <ResponsiveContainer width="100%" height={250}>
            <BarChart data={publicationsByUserData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="quantidade" fill="#FF914D" />
            </BarChart>
          </ResponsiveContainer>
        </Paper>
      </Box>

      {/* Tabela de Publicações Recentes */}
      <Box sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h6" gutterBottom>
          Publicações Recentes
        </Typography>
        {data.recentPublications.length === 0 ? (
          <Typography>Nenhuma publicação recente.</Typography>
        ) : (
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: 4,
              transition: 'transform 0.3s, box-shadow 0.3s',
              '&:hover': {
                transform: 'scale(1.01)',
                boxShadow: 8,
              },
            }}
          >
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Título</TableCell>
                  <TableCell>Publicado em</TableCell>
                  <TableCell>Resumo</TableCell>
                  <TableCell>Categorias</TableCell>
                  <TableCell>Onde?</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.recentPublications.map((pub) => {
                  const publishedDate = formatFullDateTime(pub.published_at || null);

                  // Caso o backend agora traga "categories" e flags:
                  const catItems = (pub as any).categories || [];
                  const isDom = (pub as any).is_for_dom || false;
                  const isDou = (pub as any).is_for_dou || false;
                  const isJgc = (pub as any).is_for_jgc || false;

                  return (
                    <TableRow
                      key={pub.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => handleRowClick(pub)}
                    >
                      <TableCell>{pub.title}</TableCell>
                      <TableCell>{publishedDate}</TableCell>
                      <TableCell>{pub.summary}</TableCell>
                      <TableCell>
                        {catItems.length === 0 && (
                          <Typography variant="caption">—</Typography>
                        )}
                        {catItems.map((c: any, idx: number) => (
                          <Chip
                            key={c.id}
                            label={c.category}
                            size="small"
                            variant="outlined"
                            color={colorArray[idx % colorArray.length]}
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        ))}
                      </TableCell>
                      <TableCell>
                        {isDom && (
                          <Chip
                            label="Diário Oficial (próprio)"
                            color="info"
                            variant="outlined"
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {isDou && (
                          <Chip
                            label="Diário Oficial da União"
                            color="warning"
                            variant="outlined"
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                        {isJgc && (
                          <Chip
                            label="Jornal de Grande Circulação"
                            color="error"
                            variant="outlined"
                            size="small"
                            sx={{ mr: 0.5, mb: 0.5 }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>
    </Box>
  );
};

export default Dashboard;